import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import ProductCard from '../components/ProductCard';
import { ChevronRight, ArrowLeft } from 'lucide-react';
import { EditModeProvider, AdminToolbar } from '../components/EditableContent';
import CMSBlockRenderer from '../components/CMSBlockRenderer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ProjectDetailPage() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    fetchProject();
  }, [slug]);

  const fetchProject = async () => {
    try {
      const res = await axios.get(`${API}/projects/${slug}`);
      setProject(res.data.project);
      setProducts(res.data.products);
    } catch (err) {
      console.error('Error fetching project:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-zinc-800 rounded w-1/4" />
            <div className="aspect-video bg-zinc-800 rounded" />
            <div className="h-32 bg-zinc-800 rounded" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!project) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h1 className="text-2xl font-bold text-foreground">Project not found</h1>
          <Link to="/projects" className="text-[#78BE20] hover:underline mt-4 inline-block">
            Back to Projects
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="project-detail-page">
        {/* Breadcrumb */}
        <nav className="flex items-center space-x-2 text-sm text-muted-foreground mb-8">
          <Link to="/" className="hover:text-[#78BE20]">Home</Link>
          <ChevronRight className="h-4 w-4" />
          <Link to="/projects" className="hover:text-[#78BE20]">Projects</Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-foreground">{project.title}</span>
        </nav>

        {/* Back Link */}
        <Link to="/projects" className="inline-flex items-center text-muted-foreground hover:text-[#78BE20] mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Projects
        </Link>

        {/* Title & Tags */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2 mb-4">
            {project.tags?.map(tag => (
              <span key={tag} className="text-xs px-3 py-1 bg-[#78BE20]/10 text-[#78BE20] rounded-sm">
                {tag}
              </span>
            ))}
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-foreground uppercase tracking-tight">
            {project.title}
          </h1>
        </div>

        {/* Gallery */}
        {project.gallery?.length > 0 && (
          <div className="mb-12">
            <div className="aspect-video bg-zinc-900 rounded-sm overflow-hidden mb-4">
              <img
                src={project.gallery[selectedImage]}
                alt={project.title}
                className="w-full h-full object-cover"
              />
            </div>
            {project.gallery.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {project.gallery.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`flex-shrink-0 w-24 h-16 rounded-sm overflow-hidden border-2 transition-colors ${
                      selectedImage === idx ? 'border-[#78BE20]' : 'border-border'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Description */}
        <div className="prose prose-invert max-w-none mb-12">
          <h2 className="text-xl font-bold text-foreground mb-4">About This Project</h2>
          <p className="text-muted-foreground whitespace-pre-wrap">{project.description}</p>
        </div>

        {/* Products Used */}
        {products.length > 0 && (
          <div>
            <h2 className="text-2xl font-black text-foreground uppercase tracking-tight mb-6">
              Products Used
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {products.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        )}
      </div>
      <CMSBlockRenderer excludeTypes={['hero']} />
      <AdminToolbar />
    </Layout>
  );
}

export default function ProjectDetailWrapper() {
  return (
    <EditModeProvider pageSlug="projects">
      <ProjectDetailPage />
    </EditModeProvider>
  );
}
